/**
 * QuizTestPage - 공통 퀴즈 테스트 페이지 컴포넌트
 *
 * 개발/테스트 환경에서 퀴즈 ID를 입력하여 개별 퀴즈를 로드하고 테스트하는 페이지.
 * apps/company, apps/lc 등에서 API만 주입하여 사용.
 */
import { useState, useCallback, useEffect, useMemo, useRef } from 'react'
import { Loader2, Search, RotateCcw, ChevronDown, ChevronUp, BookOpen } from 'lucide-react'
import {
  QuizRenderer,
  MultiChoiceQuizForDiary,
  FillBlankQuizForDiary,
  SelectTextQuizForDiary,
  QuizStyleProvider,
} from '../quiz-types'
import type {
  FillBlankQuizData,
  MultiChoiceQuizData,
  SelectTextQuizData,
} from '../../types/legacyQuiz'
import { useQuizTest } from './useQuizTest'
import {
  QUIZ_IDS_BY_TYPE,
  QUIZ_IDS_NEW_UI,
  QUIZ_TYPE_LABELS,
  DEFAULT_LABELS,
  isNewUiQuiz,
  QUEBOARD_QUIZ_GROUPS,
} from './constants'
import type { QuizCaseDescriptor, QuizTestPageProps, QuizTestLabels } from './types'
import { FloatingQueBoard } from '../tools/QueBoard/FloatingQueBoard'
import { getQueBoardPresetsForQuiz } from '../tools/QueBoard/quizPresets'
import { QuizCaseCatalog } from './QuizCaseCatalog'

const EMPTY_CASES: readonly QuizCaseDescriptor[] = []

/**
 * 번역 함수 헬퍼: t props가 있으면 사용, 없으면 DEFAULT_LABELS fallback
 */
function createLabels(t?: (key: string) => string): QuizTestLabels {
  if (!t) return DEFAULT_LABELS
  return {
    invalidJson: t('quizTest.invalidJson'),
    enterQuizId: t('quizTest.enterQuizId'),
    fetchFailed: t('quizTest.fetchFailed'),
    description: t('quizTest.description'),
    quickSelect: t('quizTest.quickSelect'),
    newUiByType: t('quizTest.newUiByType'),
    quizIdLabel: t('quizTest.quizIdLabel'),
    quizIdPlaceholder: t('quizTest.quizIdPlaceholder'),
    search: t('quizTest.search'),
    advancedOptions: t('quizTest.advancedOptions'),
    newUiComponent: t('quizTest.newUiComponent'),
    solving: t('quizTest.solving'),
    loading: t('quizTest.loading'),
    reset: t('quizTest.reset'),
  }
}

function getInitialDimChoiceText(): boolean {
  if (typeof window === 'undefined') return false

  const params = new URLSearchParams(window.location.search)
  return params.get('dimChoiceText') === 'true' || params.get('choiceDisplayPolicy') === 'dimmed'
}

function getInitialCaseId(cases: readonly QuizCaseDescriptor[]): string | null {
  if (cases.length === 0) return null
  if (typeof window === 'undefined') return cases[0].id

  const requestedCaseId = new URLSearchParams(window.location.search).get('case')
  return cases.some((quizCase) => quizCase.id === requestedCaseId) ? requestedCaseId : cases[0].id
}

function getUnknownCaseId(cases: readonly QuizCaseDescriptor[]): string | null {
  if (cases.length === 0 || typeof window === 'undefined') return null

  const requestedCaseId = new URLSearchParams(window.location.search).get('case')
  return requestedCaseId && !cases.some((quizCase) => quizCase.id === requestedCaseId)
    ? requestedCaseId
    : null
}

function normalizeApiCorrectAnswers(value: unknown[] | null | undefined): string[] {
  if (!Array.isArray(value)) return []

  const answers: string[] = []
  for (const answer of value) {
    if (typeof answer === 'string' || typeof answer === 'number' || typeof answer === 'boolean') {
      answers.push(String(answer))
    }
  }
  return answers.length === value.length ? answers : []
}

export function QuizTestPage({ api, t, useFixedVars: useFixedVarsProp = true, cases = EMPTY_CASES }: QuizTestPageProps) {
  const labels = createLabels(t)
  const [showAdvanced, setShowAdvanced] = useState(false)
  const [showQuickSelect, setShowQuickSelect] = useState(() => cases.length === 0)
  const [quickSelectTab, setQuickSelectTab] = useState<'type' | 'queboard'>('type')
  const [queBoardOpen, setQueBoardOpen] = useState(false)
  const [dimChoiceText, setDimChoiceText] = useState(getInitialDimChoiceText)
  const [selectedCaseId, setSelectedCaseId] = useState(() => getInitialCaseId(cases))
  const [unknownCaseId, setUnknownCaseId] = useState(() => getUnknownCaseId(cases))
  const [isDirectLookup, setIsDirectLookup] = useState(() => cases.length === 0)
  const loadedCaseIdRef = useRef<string | null>(null)
  const selectedCase = useMemo(
    () => cases.find((quizCase) => quizCase.id === selectedCaseId) ?? cases[0] ?? null,
    [cases, selectedCaseId],
  )
  const activePreviewCase = isDirectLookup ? null : selectedCase

  const {
    quizId,
    setQuizId,
    showAnswer,
    setShowAnswer,
    useFixedVars,
    setUseFixedVars,
    varsJson,
    setVarsJson,
    varsError,
    setVarsError,
    quizData,
    solveResult,
    submittedAnswers,
    isLoading,
    isSolving,
    error,
    showDebug,
    setShowDebug,
    rawResponse,
    rawSolveResponse,
    legacyQuiz,
    handleFetch,
    handleSolve,
    handleReset,
  } = useQuizTest(api, useFixedVarsProp)

  const handleQuickSelect = useCallback(
    (id: string) => {
      setIsDirectLookup(true)
      handleFetch(id, labels.fetchFailed)
    },
    [handleFetch, labels.fetchFailed]
  )

  const onFetch = useCallback(() => {
    setIsDirectLookup(true)
    handleFetch(undefined, labels.fetchFailed)
  }, [handleFetch, labels.fetchFailed])

  const handleSelectCase = useCallback((quizCase: QuizCaseDescriptor) => {
    setSelectedCaseId(quizCase.id)
    setUnknownCaseId(null)
    setIsDirectLookup(false)
    if (typeof window !== 'undefined') {
      const url = new URL(window.location.href)
      url.searchParams.set('case', quizCase.id)
      window.history.replaceState(null, '', `${url.pathname}${url.search}${url.hash}`)
    }
  }, [])

  useEffect(() => {
    if (!selectedCase) return
    if (loadedCaseIdRef.current === selectedCase.id) return
    loadedCaseIdRef.current = selectedCase.id

    handleReset()
    if (selectedCase.renderStatus === 'renderable' && selectedCase.apiQuizId) {
      const needsAnswerData = selectedCase.rendererProps?.correctAnswersSource === 'api'
      handleFetch(selectedCase.apiQuizId, labels.fetchFailed, needsAnswerData
        ? { showAnswer: true, includeSolveResult: true }
        : undefined)
    }
  }, [handleFetch, handleReset, labels.fetchFailed, selectedCase])

  const expectedApiType = activePreviewCase?.declaredType === 'short_answer'
    ? 'fill_blank'
    : activePreviewCase?.declaredType
  const hasTypeMismatch = Boolean(
    activePreviewCase
      && quizData
      && expectedApiType
      && quizData.type !== expectedApiType,
  )
  const apiCorrectAnswers = useMemo(
    () => normalizeApiCorrectAnswers(quizData?.correctAnswers),
    [quizData?.correctAnswers],
  )
  const hasMissingAnswerData = Boolean(
    activePreviewCase?.rendererProps?.correctAnswersSource === 'api'
      && quizData
      && apiCorrectAnswers.length === 0,
  )

  return (
    <div className={`p-4 mx-auto space-y-6 md:p-6 ${cases.length > 0 ? 'max-w-7xl' : 'max-w-4xl'}`}>
      {unknownCaseId ? (
        <div className="rounded-2xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-950">
          <strong>{t ? t('quizCatalog.errors.unknownCaseTitle') : 'Unknown case link'}</strong>{' '}
          <span>{t ? t('quizCatalog.errors.unknownCase') : 'The requested case was not found, so the first case is shown.'}</span>{' '}
          <code>{unknownCaseId}</code>
        </div>
      ) : null}
      {selectedCase ? (
        <QuizCaseCatalog
          cases={cases}
          selectedCase={selectedCase}
          onSelect={handleSelectCase}
          t={t}
        />
      ) : null}
      {cases.length === 0 ? (
        <>
          <h1 className="text-2xl font-bold text-gray-900">Quiz Test Page</h1>
          <p className="text-sm text-gray-600">{labels.description}</p>
        </>
      ) : (
        <div>
          <h2 className="text-lg font-bold text-gray-900">{t ? t('quizCatalog.directLookup') : 'Direct API lookup'}</h2>
          <p className="mt-1 text-sm text-gray-600">{labels.description}</p>
        </div>
      )}

      {/* Search Form */}
      <div className="p-6 bg-white rounded-lg border border-gray-200 shadow-2xs">
        <div className="space-y-4">
          {/* Quick Select Section */}
          <div className="space-y-3">
            <button
              type="button"
              onClick={() => setShowQuickSelect(!showQuickSelect)}
              className="flex gap-2 items-center text-sm font-medium text-gray-700 transition-colors hover:text-gray-900"
            >
              {showQuickSelect ? (
                <ChevronUp className="w-4 h-4" />
              ) : (
                <ChevronDown className="w-4 h-4" />
              )}
              <BookOpen className="w-4 h-4" />
              {labels.quickSelect}
            </button>

            {showQuickSelect && (
              <div className="pt-2 pb-4 border-b border-gray-200">
                {/* Tabs */}
                <div className="flex gap-1 mb-3 border-b border-gray-200">
                  <button
                    type="button"
                    onClick={() => setQuickSelectTab('type')}
                    className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors -mb-px
                      ${quickSelectTab === 'type'
                        ? 'border-indigo-600 text-indigo-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
                  >
                    문제유형
                  </button>
                  <button
                    type="button"
                    onClick={() => setQuickSelectTab('queboard')}
                    className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors -mb-px
                      ${quickSelectTab === 'queboard'
                        ? 'border-indigo-600 text-indigo-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
                  >
                    깨보드
                  </button>
                </div>

                {/* 문제유형 탭 */}
                {quickSelectTab === 'type' && (
                  <div className="space-y-3">
                    {/* 새 UI 전용 퀴즈 */}
                    <div className="p-3 space-y-2 bg-amber-50 rounded-lg border border-amber-200">
                      <label className="text-xs font-semibold text-amber-800 uppercase">
                        {labels.newUiByType}
                      </label>
                      <div className="flex flex-wrap gap-2">
                        {Object.entries(QUIZ_IDS_NEW_UI).map(([type, ids]) =>
                          ids.map((id) => (
                            <button
                              key={id}
                              onClick={() => handleQuickSelect(id)}
                              disabled={isLoading}
                              className={`px-3 py-1.5 text-xs font-mono rounded-md border transition-colors
                                ${quizId === id
                                  ? 'bg-amber-600 text-white border-amber-600'
                                  : 'bg-white text-amber-800 border-amber-300 hover:border-amber-400 hover:bg-amber-100'}
                                ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                            >
                              {QUIZ_TYPE_LABELS[type as keyof typeof QUIZ_TYPE_LABELS]} · {id}
                            </button>
                          ))
                        )}
                      </div>
                    </div>
                    {Object.entries(QUIZ_IDS_BY_TYPE).map(([type, ids]) => (
                      <div key={type} className="space-y-2">
                        <label className="text-xs font-semibold text-gray-600 uppercase">
                          {QUIZ_TYPE_LABELS[type as keyof typeof QUIZ_TYPE_LABELS]}
                        </label>
                        <div className="flex flex-wrap gap-2">
                          {ids.map((id) => (
                            <button
                              key={id}
                              onClick={() => handleQuickSelect(id)}
                              disabled={isLoading}
                              className={`px-3 py-1.5 text-xs font-mono rounded-md border transition-colors
                                ${quizId === id
                                  ? 'bg-indigo-600 text-white border-indigo-600'
                                  : 'bg-white text-gray-700 border-gray-300 hover:border-indigo-400 hover:bg-indigo-50'}
                                ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                            >
                              {id}
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* 깨보드 탭 */}
                {quickSelectTab === 'queboard' && (
                  <div className="space-y-3">
                    {Object.entries(QUEBOARD_QUIZ_GROUPS).map(([group, ids]) => (
                      <div key={group} className="space-y-2">
                        <label className="text-xs font-semibold text-gray-600">
                          {group}
                        </label>
                        <div className="flex flex-wrap gap-2">
                          {ids.map((id) => (
                            <button
                              key={id}
                              onClick={() => handleQuickSelect(id)}
                              disabled={isLoading}
                              className={`px-3 py-1.5 text-xs font-mono rounded-md border transition-colors
                                ${quizId === id
                                  ? 'bg-emerald-600 text-white border-emerald-600'
                                  : 'bg-white text-emerald-800 border-emerald-300 hover:border-emerald-400 hover:bg-emerald-50'}
                                ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                            >
                              {id}
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Quiz ID Input */}
          <div className="flex gap-3">
            <div className="flex-1">
              <label htmlFor="quizId" className="sr-only">
                {labels.quizIdLabel}
              </label>
              <input
                id="quizId"
                type="text"
                placeholder={labels.quizIdPlaceholder}
                value={quizId}
                onChange={(e) => setQuizId(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && onFetch()}
                className="px-4 py-2 w-full text-lg rounded-lg border border-gray-300 focus:outline-hidden focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
            </div>
            <button
              onClick={onFetch}
              disabled={isLoading}
              className="flex gap-2 items-center px-6 py-2 font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Search className="w-4 h-4" />
              )}
              {labels.search}
            </button>
          </div>

          {/* Advanced Options Toggle */}
          <button
            type="button"
            onClick={() => setShowAdvanced(!showAdvanced)}
            className="flex gap-2 items-center text-sm text-gray-600 transition-colors hover:text-gray-900"
          >
            {showAdvanced ? (
              <ChevronUp className="w-4 h-4" />
            ) : (
              <ChevronDown className="w-4 h-4" />
            )}
            {labels.advancedOptions}
          </button>

          {/* Advanced Options Panel */}
          {showAdvanced && (
            <div className="pt-4 space-y-4 border-t border-gray-200">
              <div className="flex gap-4 items-center">
                <label className="flex gap-2 items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={showAnswer}
                    onChange={(e) => setShowAnswer(e.target.checked)}
                    className="w-4 h-4 text-indigo-600 rounded-xs border-gray-300 focus:ring-indigo-500"
                  />
                  <span className="text-sm text-gray-700">showAnswer</span>
                </label>
                <label className="flex gap-2 items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={useFixedVars}
                    onChange={(e) => setUseFixedVars(e.target.checked)}
                    className="w-4 h-4 text-indigo-600 rounded-xs border-gray-300 focus:ring-indigo-500"
                  />
                  <span className="text-sm text-gray-700">useFixedVars</span>
                </label>
                <label className="flex gap-2 items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={dimChoiceText}
                    onChange={(e) => setDimChoiceText(e.target.checked)}
                    className="w-4 h-4 text-indigo-600 rounded-xs border-gray-300 focus:ring-indigo-500"
                  />
                  <span className="text-sm text-gray-700">dimChoiceText</span>
                </label>
              </div>

              <div className="space-y-2">
                <label htmlFor="varsJson" className="text-sm font-medium text-gray-700">
                  vars (JSON)
                </label>
                <textarea
                  id="varsJson"
                  placeholder='{"key": "value"}'
                  value={varsJson}
                  onChange={(e) => {
                    setVarsJson(e.target.value)
                    setVarsError(null)
                  }}
                  className={`w-full px-3 py-2 font-mono text-sm border rounded-lg focus:outline-hidden focus:ring-2 focus:ring-indigo-500 ${
                    varsError ? 'border-red-500' : 'border-gray-300'
                  }`}
                  rows={3}
                />
                {varsError && <p className="text-sm text-red-600">{varsError}</p>}
              </div>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="p-3 bg-red-50 rounded-lg border border-red-200">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}
        </div>
      </div>

      {quizData && hasTypeMismatch ? (
        <div className="rounded-2xl border border-rose-300 bg-rose-50 p-5 text-rose-950">
          <h3 className="text-lg font-black">{t ? t('quizCatalog.errors.typeMismatchTitle') : 'Type mismatch'}</h3>
          <p className="mt-1 text-sm">
            {t ? t('quizCatalog.errors.typeMismatch') : 'The real API type does not match this case.'}{' '}
            <span className="font-mono">Expected {expectedApiType} / Actual {quizData.type}</span>
          </p>
        </div>
      ) : null}

      {quizData && hasMissingAnswerData ? (
        <div className="rounded-2xl border border-amber-300 bg-amber-50 p-5 text-amber-950">
          <h3 className="text-lg font-black">{t ? t('quizCatalog.errors.answerDataUnavailableTitle') : 'Answer data unavailable'}</h3>
          <p className="mt-1 text-sm">
            {t ? t('quizCatalog.errors.answerDataUnavailable') : 'This answer-key case requires correctAnswers from the real API response.'}
          </p>
        </div>
      ) : null}

      {/* Quiz Display */}
      {quizData && legacyQuiz && !hasTypeMismatch && !hasMissingAnswerData && (
        <div className="p-6 bg-white rounded-lg border border-gray-200 shadow-2xs">
          {/* Quiz Header */}
          <div className="flex justify-between items-center pb-4 mb-6 border-b border-gray-200">
            <div className="space-y-1">
              <p className="text-sm text-gray-500">
                Quiz ID: <span className="font-mono">{quizData.quizId}</span>
              </p>
              <p className="text-sm text-gray-500">
                Type:{' '}
                <span className="font-mono px-2 py-0.5 bg-gray-100 rounded-xs">
                  {legacyQuiz.type}
                </span>
              </p>
            </div>
            <div className="flex gap-2 items-center">
              <button
                onClick={() => setShowDebug(!showDebug)}
                className="px-3 py-1.5 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                {showDebug ? 'Hide Debug' : 'Show Debug'}
              </button>
              <button
                onClick={handleReset}
                className="px-3 py-1.5 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-2"
              >
                <RotateCcw className="w-4 h-4" />
                {labels.reset}
              </button>
            </div>
          </div>

          {/* Debug Info */}
          {showDebug && (
            <div className="mb-6 space-y-4">
              <div className="p-4 bg-gray-50 rounded-lg">
                <h4 className="mb-2 text-sm font-semibold text-gray-700">Raw Quiz Response:</h4>
                <pre className="overflow-auto max-h-64 text-xs text-gray-600">
                  {JSON.stringify(rawResponse, null, 2)}
                </pre>
              </div>
              {rawSolveResponse !== null && (
                <div className="p-4 bg-gray-50 rounded-lg">
                  <h4 className="mb-2 text-sm font-semibold text-gray-700">Raw Solve Response:</h4>
                  <pre className="overflow-auto max-h-64 text-xs text-gray-600">
                    {JSON.stringify(rawSolveResponse, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          )}

          {/* QueBoard Tool */}
          {(() => {
            const presets = getQueBoardPresetsForQuiz(quizData.quizId ?? '')
            if (!presets) return null
            return (
              <>
                <button
                  onClick={() => setQueBoardOpen(true)}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium bg-indigo-50 text-indigo-700 border border-indigo-200 rounded-lg hover:bg-indigo-100 transition-colors mb-3"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2" /><path d="M3 9h18M9 3v18" /></svg>
                  깨구단 열기
                </button>
                {queBoardOpen && (
                  <FloatingQueBoard
                    problems={presets}
                    onClose={() => setQueBoardOpen(false)}
                  />
                )}
              </>
            )
          })()}

          {/* Quiz Content */}
          {(() => {
            const currentQuizId = quizData.quizId ?? ''
            const isNewUi = !activePreviewCase && isNewUiQuiz(currentQuizId)

            if (isNewUi) {
              return (
                <div
                  className="quiz-new-ui min-h-[300px] rounded-xl border-2 border-amber-300 bg-amber-50/60 p-4"
                  data-quiz-ui="new"
                  data-quiz-id={currentQuizId}
                >
                  <div className="mb-4 text-xs font-semibold tracking-wide text-amber-700 uppercase">
                    {labels.newUiComponent}
                  </div>
                  {legacyQuiz.type === 'fill_blank' && (
                    <FillBlankQuizForDiary
                      key={`new-${quizData.quizId}-${solveResult ? 'solved' : 'unsolved'}`}
                      quizId={quizData.quizId ?? ''}
                      quiz={legacyQuiz as FillBlankQuizData}
                      onSolve={handleSolve}
                      solveResult={solveResult}
                      showReallyTrue={false}
                      initialAnswers={submittedAnswers.length > 0 ? submittedAnswers : undefined}
                    />
                  )}
                  {legacyQuiz.type === 'multi_choice' && (
                    <MultiChoiceQuizForDiary
                      key={`new-${quizData.quizId}-${solveResult ? 'solved' : 'unsolved'}`}
                      quizId={quizData.quizId ?? ''}
                      quiz={legacyQuiz as MultiChoiceQuizData}
                      onSolve={handleSolve}
                      solveResult={solveResult}
                      showReallyTrue={false}
                      initialAnswers={submittedAnswers.length > 0 ? submittedAnswers : undefined}
                    />
                  )}
                  {legacyQuiz.type === 'select_text' && (
                    <SelectTextQuizForDiary
                      key={`new-${quizData.quizId}-${solveResult ? 'solved' : 'unsolved'}`}
                      quizId={quizData.quizId ?? ''}
                      quiz={legacyQuiz as SelectTextQuizData}
                      onSolve={handleSolve}
                      solveResult={solveResult}
                      showReallyTrue={false}
                      initialAnswers={submittedAnswers.length > 0 ? submittedAnswers : undefined}
                    />
                  )}
                </div>
              )
            }

            const rendererProps = activePreviewCase?.rendererProps

            return (
              <QuizStyleProvider variant={activePreviewCase?.variant ?? 'default'}>
                <div className="min-h-[300px]">
                  <QuizRenderer
                    key={`${activePreviewCase?.id ?? 'direct'}-${quizData.quizId}-${solveResult ? 'solved' : 'unsolved'}`}
                    quizType={activePreviewCase?.rendererType ?? legacyQuiz.type}
                    quizId={quizData.quizId ?? ''}
                    quiz={legacyQuiz}
                    onSolve={handleSolve}
                    solveResult={solveResult}
                    mode={activePreviewCase?.mode}
                    showReallyTrue={rendererProps?.showReallyTrue ?? false}
                    isViewSolved={rendererProps?.isViewSolved}
                    showCorrectAnswers={rendererProps?.showCorrectAnswers}
                    correctAnswers={apiCorrectAnswers.length > 0 ? apiCorrectAnswers : undefined}
                    hideSubmitButton={rendererProps?.hideSubmitButton}
                    disableSound={rendererProps?.disableSound}
                    number={rendererProps?.number}
                    useKeypad={rendererProps?.useKeypad ?? false}
                    compact={rendererProps?.compact}
                    hideExplanation={rendererProps?.hideExplanation}
                    hideSolveDescription={rendererProps?.hideSolveDescription}
                    initialAnswers={submittedAnswers.length > 0 ? submittedAnswers : undefined}
                    dimChoiceText={rendererProps?.dimChoiceText ?? dimChoiceText}
                  />
                </div>
              </QuizStyleProvider>
            )
          })()}

          {/* Solving indicator */}
          {isSolving && (
            <div className="flex gap-2 justify-center items-center mt-4 text-gray-500">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span className="text-sm">{labels.solving}</span>
            </div>
          )}
        </div>
      )}

      {/* Loading State */}
      {isLoading && (
        <div className="p-6 bg-white rounded-lg border border-gray-200 shadow-2xs">
          <div className="flex justify-center items-center py-12">
            <div className="flex flex-col gap-4 items-center">
              <Loader2 className="w-8 h-8 text-indigo-600 animate-spin" />
              <p className="text-gray-600">{labels.loading}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
